import { useCallback, useEffect, useState } from 'react';

/**
 * Which COLUMN GROUPS a table draws, persisted per module.
 *
 * A banded table can carry more columns than any one reader uses — Stock Levels runs to
 * twenty-five under six headings — and the groups they never read cost them the ones they do,
 * because every hidden column is width the rest of the row gets back. This is the filter rail's
 * `useFacetVisibility` one surface along: a LAYOUT preference, not a filter.
 *
 * **Persisting it is safe for the reason orientation is, and Show deleted is not.** It changes
 * how the same records are ARRANGED rather than which records they are, so a remembered value
 * has nothing to get wrong. A remembered record filter is how a deleted row comes to be treated
 * as live; a remembered column set is a reader finding the table they left.
 *
 * **It can never hide the column a list is SORTED by.** `isVisible` overrules the stored
 * preference for the group holding the active sort, which is the same override
 * `useFacetVisibility` applies to an axis carrying a constraint — and for the same reason: a
 * table ordered by something with no header on screen is a table whose order the reader cannot
 * explain or undo. Deriving it rather than clearing the sort on hide is what keeps a layout
 * control from being destructive.
 */
function storageKey(key: string, store: string): string {
  return `index.${key}.${store}`;
}

function read(key: string, store: string): string[] {
  try {
    const stored = localStorage.getItem(storageKey(key, store));
    if (stored === null) return [];

    const parsed: unknown = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    // Blocked storage, or a value written by an older shape — show everything. A table that
    // came back with columns missing because site data could not be read is worse than one
    // that forgot a preference.
    return [];
  }
}

/**
 * The mechanism both of the cog's put-away sections run on — columns, and the alert cards.
 *
 * <b>It is ONE hook with two stores rather than two hooks</b>, because everything about the
 * behaviour is the same: a set of keys the reader has put away, persisted per module, with an
 * override that keeps a key on screen while something else depends on it being there. What
 * differs is only what the override MEANS — the sorted column for a header group, the ticked
 * tile for a card — and the caller supplies that as a boolean.
 */
function useHiddenSet(key: string, store: string) {
  const [hidden, setHidden] = useState<string[]>(() => read(key, store));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key, store), JSON.stringify(hidden));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, store, hidden]);

  const toggle = useCallback((groupKey: string) => {
    setHidden((current) =>
      current.includes(groupKey) ? current.filter((k) => k !== groupKey) : [...current, groupKey],
    );
  }, []);

  /**
   * Whether the table draws this group.
   *
   * `sorted` is the override — see the remarks. A group holding the column the list is ordered
   * by stays on screen whatever the stored preference says.
   */
  const isVisible = useCallback(
    (groupKey: string, keepAnyway: boolean) => keepAnyway || !hidden.includes(groupKey),
    [hidden],
  );

  return { hidden, toggle, isVisible };
}

/** Which COLUMN GROUPS a table draws. The override is the SORTED column — see the remarks. */
export function useHeaderGroups(key: string) {
  return useHiddenSet(key, 'hiddenHeaderGroups');
}

/**
 * Which ALERT CARDS a page draws, persisted per module.
 *
 * <b>The same trade the columns make, one band up.</b> Stock Levels files eight tiles by tab
 * already — a Lease Ending tile over a list of consumables can only ever read zero — and what
 * is left is still eight on the All tab, in two rows, above a table the reader came for. This
 * is the reader's own say over that, asked for in so many words: <i>in the dropdown settings,
 * add a toggle as well to customized the displayed cards, same as the columns.</i>
 *
 * <b>Its override is the TICKED tile, not the sorted column.</b> A card carrying the list's
 * current selection is drawn whatever the stored preference says — which is the rule the tiles
 * already run on for the tab filing, and for the same reason: a selection the reader cannot see
 * is a list narrowed by a control that is not on screen, and unticking it would be impossible.
 */
export function useCardGroups(key: string) {
  return useHiddenSet(key, 'hiddenCards');
}
